import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radar, Sparkles, TrendingDown, ArrowRight, Clock, Trash2, Compass, Check, ShieldCheck, History } from "lucide-react";
import { Shell } from "../components/Shell";
import { api, eur } from "../lib/api";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { tripDateContext } from "../lib/goal";
import { loadLiveWatch, clearLiveWatch } from "../lib/selectedTrip";
import { loadXpWatch, clearXpWatch } from "../lib/xpPlan";

export default function Watch() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [items, setItems] = useState(null);
  const [opp, setOpp] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [liveWatch, setLiveWatch] = useState(() => loadLiveWatch());
  const [xpWatch, setXpWatch] = useState(() => loadXpWatch());
  const liveWatchDates = tripDateContext(liveWatch, lang);

  const load = () => api.watchlist().then((r) => setItems(r.items)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.watchTimeline().then((r) => setTimeline(r.events)).catch(() => setTimeline([]));
  }, [lang]);

  const simulate = async () => {
    setLoading(true);
    try {
      const r = await api.simulateOpportunity();
      setOpp(r); await load();
      setTimeout(() => document.getElementById("opp-banner")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
    } catch { toast.error(t("watch.simErr")); } finally { setLoading(false); }
  };

  const remove = async (tid) => {
    await api.removeWatch(tid); load();
    if (opp?.opportunity?.trip_id === tid) setOpp(null);
    toast(t("watch.removed"));
  };

  const removeLiveWatch = () => {
    clearLiveWatch();
    setLiveWatch(null);
    toast(t("watch.removed"));
  };

  const removeXpWatch = () => {
    clearXpWatch();
    setXpWatch(null);
    toast(t("watch.removed"));
  };

  const statusLabel = (s) => (s === "opportunity" ? t("watch.opportunityFound") : t("watch.monitoring"));

  return (
    <Shell active="watch">
      <div className="mb-5">
        <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.24em] text-slate-400 font-display"><span style={{ color: "var(--brand)" }}>{t("watch.kicker")}</span></div>
        <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{t("watch.title")}</h1>
        <p className="mt-2 text-slate-500 text-sm max-w-xl">{t("watch.sub")}</p>
      </div>

      <AnimatePresence>
        {opp && (
          <motion.div id="opp-banner" initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden dv-card mb-6" data-testid="opportunity-banner" style={{ background: "var(--ink)", border: "none" }}>
            <div className="dv-grain absolute inset-0 opacity-30" />
            <div className="relative p-5 sm:p-7 text-white">
              <div className="flex items-center gap-2 dv-pill text-[11px] px-3 py-1.5 w-max" style={{ background: "rgba(16,185,129,0.18)", color: "#6EE7B7" }}>
                <Sparkles size={13} /> {t("watch.betterOpp")}
              </div>
              <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-5 items-center">
                <div>
                <div className="flex items-center gap-3">
                  <div className="font-display font-extrabold text-3xl">{opp.opportunity.destination}</div>
                  <span className="dv-pill text-[10px] px-2.5 py-1 font-display" style={{ background: "rgba(16,185,129,0.2)", color: "#6EE7B7" }}>{t("verdict.switch")}</span>
                </div>
                  <div className="mt-3 flex items-end gap-4 flex-wrap">
                    <div><div className="text-[11px] text-white/50">{t("watch.was")}</div><div className="dv-total text-xl text-white/50 line-through">{eur(opp.opportunity.was)}</div></div>
                    <ArrowRight size={20} className="mb-1 text-white/40" />
                    <div><div className="text-[11px] text-white/60">{t("watch.now")}</div><div className="dv-total text-4xl text-white">{eur(opp.opportunity.now)}</div></div>
                    <div className="dv-pill px-3 py-1.5 mb-1 text-sm" style={{ background: "rgba(16,185,129,0.2)", color: "#6EE7B7" }}><TrendingDown size={15} /> {t("watch.save", { x: eur(opp.opportunity.savings) })}</div>
                  </div>
                </div>
                <div className="w-full sm:w-40 h-28 rounded-2xl overflow-hidden"><img src={opp.image} alt={opp.opportunity.destination} className="w-full h-full object-cover" /></div>
              </div>
              <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                {opp.opportunity.reasons.map((r, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-white/80"><Check size={14} color="#6EE7B7" /> {r}</li>
                ))}
              </ul>
              <button onClick={() => navigate(`/trip/${opp.opportunity.trip_id}`)} data-testid="review-opportunity-button" className="dv-btn text-base px-6 py-3.5 mt-6" style={{ background: "#fff", color: "var(--ink)" }}>
                {t("watch.reviewRec")} <ArrowRight size={17} />
              </button>
              <div className="mt-3 text-xs text-white/50">{t("watch.decisionChangedPre")} <span className="text-white/80 font-semibold">{t("watch.decisionChangedWord")}</span> {t("watch.decisionChangedPost")}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {items === null ? (
        <div className="space-y-3">{["watch-skeleton-a", "watch-skeleton-b"].map((key) => <div key={key} className="dv-skeleton h-24 rounded-2xl" />)}</div>
      ) : xpWatch ? (
        <div className="dv-card p-5 mb-4" data-testid="xp-watch-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] font-semibold tracking-[0.2em] text-slate-400 font-display">{t("watch.xpObjective").toUpperCase()}</div>
              <div className="mt-1.5 font-display font-bold text-xl">
                {xpWatch.plan?.xp_total || 0} XP · {xpWatch.plan?.trip_count || 0} {t("loyalty.runCount", { n: xpWatch.plan?.trip_count || 0 })}
              </div>
              <div className="mt-1 text-sm text-slate-500">
                {t("watch.xpTarget", { x: xpWatch.context?.target_xp || xpWatch.plan?.target_xp || "—" })} · {t("watch.xpBudget", { b: eur(xpWatch.context?.budget || xpWatch.plan?.total_cost || 0) })}
              </div>
              <div className="mt-3 text-[11px] text-slate-400 flex items-center gap-1"><Clock size={11} /> {t("watch.lastAnalysis")} · {t("watch.today")}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-slate-400 font-display tracking-widest">{t("common.total")}</div>
              <div className="dv-total text-2xl text-slate-900">{eur(xpWatch.plan?.total_cost || 0)}</div>
              <div className="text-xs mt-1" style={{ color: "var(--book-ink)" }}>+{xpWatch.plan?.xp_total || 0} XP {t("loyalty.projected")}</div>
            </div>
          </div>
          <div className="mt-4 border-t border-[var(--line)] pt-4">
            <div className="text-[11px] font-semibold tracking-[0.2em] text-slate-400 font-display mb-2.5">{t("watch.activeWatchTitle").toUpperCase()}</div>
            <div className="flex flex-wrap gap-2">
              {["xpW1", "xpW2", "xpW3"].map((k) => (
                <span key={k} className="dv-pill text-xs px-2.5 py-1" style={{ background: "var(--paper)", border: "1px solid var(--line)", color: "var(--ink-soft)" }}><Radar size={11} color="var(--brand)" /> {t(`watch.${k}`)}</span>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <button onClick={() => navigate("/loyalty/decision")} className="text-xs font-semibold" style={{ color: "var(--brand)" }}>{t("watch.reviewXpPlan")} →</button>
              <button onClick={removeXpWatch} data-testid="remove-xp-watch" className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-[var(--alert)] hover:bg-[var(--alert-soft)] transition-colors"><Trash2 size={15} /></button>
            </div>
          </div>
        </div>
      ) : items.length === 0 && !liveWatch ? (
        <div className="dv-card p-8 text-center" data-testid="watch-empty">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center" style={{ background: "var(--brand-soft)" }}><Radar size={26} color="var(--brand)" /></div>
          <div className="mt-4 font-display font-bold text-lg">{t("watch.emptyTitle")}</div>
          <p className="mt-1.5 text-sm text-slate-500 max-w-sm mx-auto">{t("watch.emptyBody")}</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <button onClick={() => navigate("/plan")} data-testid="empty-plan-button" className="dv-btn dv-btn-primary text-sm px-5 py-2.5"><Compass size={15} /> {t("watch.planTrip")}</button>
            <button onClick={simulate} disabled={loading} data-testid="simulate-opportunity-button" className="dv-btn dv-btn-ghost text-sm px-5 py-2.5"><Sparkles size={15} color="var(--brand)" /> {t("watch.seeMonitoringDemo")}</button>
          </div>
        </div>
      ) : liveWatch ? (
        <>
          <div className="dv-card p-5 mb-4" data-testid="live-watch-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[11px] font-semibold tracking-[0.2em] text-slate-400 font-display">{t("watch.objectiveTitle").toUpperCase()}</div>
                <div className="mt-1.5 font-display font-bold text-xl">{liveWatch.destination}</div>
                {liveWatchDates && <div className="mt-1 text-sm text-slate-500">{liveWatchDates.label}</div>}
                <div className="mt-3 text-[11px] text-slate-400 flex items-center gap-1"><Clock size={11} /> {t("watch.lastAnalysis")} · {t("watch.today")}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-slate-400 font-display tracking-widest">{t("common.total")}</div>
                <div className="dv-total text-2xl text-slate-900">{eur(liveWatch.total)}</div>
                <span className="mt-2 inline-flex dv-pill px-2.5 py-1 text-xs font-display" style={{ background: liveWatch.decision === "BOOK" ? "var(--book-soft)" : "var(--wait-soft)", color: liveWatch.decision === "BOOK" ? "var(--book-ink)" : "var(--wait-ink)" }}>
                  {t(`verdict.${liveWatch.decision === "BOOK" ? "book" : "wait"}`)}
                </span>
              </div>
            </div>
            <div className="mt-4 border-t border-[var(--line)] pt-4">
              <div className="text-[11px] font-semibold tracking-[0.2em] text-slate-400 font-display mb-2.5">{t("watch.activeWatchTitle").toUpperCase()}</div>
              <div className="flex flex-wrap gap-2">
                {["w1", "w2", "w3", "w4", "w5"].map((k) => (
                  <span key={k} className="dv-pill text-xs px-2.5 py-1" style={{ background: "var(--paper)", border: "1px solid var(--line)", color: "var(--ink-soft)" }}><Radar size={11} color="var(--brand)" /> {t(`watch.${k}`)}</span>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="text-xs text-slate-500 flex items-center gap-1.5"><ShieldCheck size={13} color="var(--book)" /> {t("watch.liveWatchNote")}</div>
                <button onClick={removeLiveWatch} data-testid="remove-live-watch" className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-[var(--alert)] hover:bg-[var(--alert-soft)] transition-colors"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="dv-card p-5 mb-4" data-testid="active-objective-card">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] font-semibold tracking-[0.2em] text-slate-400 font-display">{t("watch.objectiveTitle").toUpperCase()}</div>
                <div className="mt-1.5 font-display font-bold text-lg leading-snug">{t("watch.objectiveBody")}</div>
                <div className="mt-3 text-[11px] text-slate-400 flex items-center gap-1"><Clock size={11} /> {t("watch.lastAnalysis")} · {t("watch.today")}</div>
              </div>
              <div className="rounded-2xl p-4" style={{ background: "var(--book-soft)" }}>
                <div className="text-[11px] font-semibold" style={{ color: "var(--book-ink)" }}>{t("watch.currentDecision")}</div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="dv-pill px-2.5 py-1 text-xs font-display" style={{ background: "#fff", color: "var(--book-ink)" }}>{t("verdict.book")}</span>
                  <span className="font-display font-bold">Tenerife</span>
                  <span className="dv-total ml-auto text-lg text-slate-900">{eur(2284)}</span>
                </div>
              </div>
            </div>
            <div className="mt-4 border-t border-[var(--line)] pt-4">
              <div className="text-[11px] font-semibold tracking-[0.2em] text-slate-400 font-display mb-2.5">{t("watch.activeWatchTitle").toUpperCase()}</div>
              <div className="flex flex-wrap gap-2">
                {["w1", "w2", "w3", "w4", "w5"].map((k) => (
                  <span key={k} className="dv-pill text-xs px-2.5 py-1" style={{ background: "var(--paper)", border: "1px solid var(--line)", color: "var(--ink-soft)" }}><Radar size={11} color="var(--brand)" /> {t(`watch.${k}`)}</span>
                ))}
              </div>
              <div className="mt-3 text-xs text-slate-500 flex items-center gap-1.5"><ShieldCheck size={13} color="var(--book)" /> {t("watch.monitorsDecision")}</div>
            </div>
          </div>

          {timeline.length > 0 && (
            <div className="dv-card p-5 mb-4" data-testid="watch-timeline">
              <div className="flex items-center gap-2.5 mb-1">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "var(--brand-soft)" }}><History size={16} color="var(--brand)" /></div>
                <div className="font-display font-bold text-lg leading-tight">{t("watch.timelineTitle")}</div>
              </div>
              <p className="text-sm text-slate-500 mb-4">{t("watch.timelineSub")}</p>
              <ol className="relative">
                {timeline.map((e, i) => {
                  const dot = e.kind === "positive" ? "var(--book)" : e.kind === "start" ? "var(--brand)" : "var(--line-strong)";
                  const last = i === timeline.length - 1;
                  return (
                    <li key={i} className="relative pl-7 pb-5 last:pb-0" data-testid={`timeline-event-${i}`}>
                      {!last && <span className="absolute left-[7px] top-3 bottom-0 w-px" style={{ background: "var(--line)" }} />}
                      <span className="absolute left-0 top-1.5 w-3.5 h-3.5 rounded-full ring-4 ring-white" style={{ background: dot }} />
                      <div className="text-[11px] font-semibold tracking-wide text-slate-400 font-display flex items-center gap-1"><Clock size={10} /> {e.when}</div>
                      <div className="mt-1 text-sm text-slate-700 leading-relaxed">{e.text}</div>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.trip_id} className="dv-card p-4 flex items-center justify-between gap-3" data-testid={`watch-item-${it.trip_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: it.status === "opportunity" ? "var(--book-soft)" : "var(--brand-soft)" }}>
                    {it.status === "opportunity" ? <TrendingDown size={18} color="var(--book-ink)" /> : <Radar size={18} color="var(--brand)" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold truncate">{it.destination}</div>
                    <div className="text-[11px] text-slate-400 flex items-center gap-1"><Clock size={11} /> {t("watch.lastCheckedNow", { s: statusLabel(it.status) })}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <div className="text-[10px] text-slate-400 font-display tracking-widest">{t("common.total")}</div>
                    <div className="dv-total text-lg">{eur(it.total)}</div>
                  </div>
                  <button onClick={() => remove(it.trip_id)} data-testid={`watch-remove-${it.trip_id}`} className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-[var(--alert)] hover:bg-[var(--alert-soft)] transition-colors"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
          <button onClick={simulate} disabled={loading} data-testid="simulate-opportunity-button" className="dv-btn dv-btn-primary w-full text-base py-4 mt-5">
            <Sparkles size={17} /> {loading ? t("watch.scanning") : t("watch.simulate")}
          </button>
        </>
      )}
    </Shell>
  );
}
